/**
 * Confirm Dialog – default shell is a structured panel (header / body / footer, dividers).
 * Footer: primary then optional neutral then secondary, grouped and centered; backdrop/X call onDismiss or onSecondary.
 * Use shell="applicant" for the compact applicant-module layout (modals.module.css).
 *
 * **Confirm style across modules (default):** panel like company disable — title + close (X) in header with divider,
 * left-aligned body, centered **Confirm** (solid blue) + **Cancel** (outline) in the footer. Prefer this over
 * `window.confirm`.
 *
 * Yes/No confirms default to the shared Save Changes-style shell. Older
 * `centeredPillYesNo` callers are treated as the same shared presentation.
 */

import { createPortal } from "react-dom";
import { useEffect } from "react";
import { FileText, X } from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { OnScreenModal } from "@/components/ui/OnScreenModal";
import modalStyles from "@/features/application/styles/modals.module.css";
import claimsModalStyles from "@/components/ui/claimsModals.module.css";
import ledgerStyles from "@/features/resident/components/detail/PaymentLedgerFinalLedgerDialog.module.css";

/**
 * JSX string attributes (message="a\nb") keep literal `\n` under esbuild;
 * JS expressions (message={"a\nb"}) produce real newlines. Normalize both.
 */
function normalizeDialogMessageNewlines(message: string): string {
  return message.replace(/\\n/g, "\n");
}

/** Applicant-style body; `align="center"` for shared Yes/No confirms. */
function DialogMessageBodyApplicant({
  message,
  layout,
  align = "left",
  weight = "medium",
}: {
  message: string;
  layout: "default" | "largeFile" | "listingConfirm";
  align?: "left" | "center";
  /** Body text weight — `medium` matches the long-standing default; `normal` for longer, multi-paragraph copy that shouldn't compete with the title. */
  weight?: "normal" | "medium";
}) {
  const normalizedMessage = normalizeDialogMessageNewlines(message);
  const alignClass = align === "center" ? "text-center" : "text-left";
  const weightClass = weight === "normal" ? "font-normal" : "font-medium";
  if (layout === "listingConfirm") {
    const parts = normalizedMessage.trim().split(/\n\n/).filter(Boolean);
    if (parts.length >= 2) {
      const question = parts[parts.length - 1];
      const body = parts.slice(0, -1).join("\n\n");
      return (
        <div
          id="confirm-dialog-description"
          className={`space-y-3 text-sm leading-relaxed text-[#232946] ${alignClass}`}
        >
          <p className={`${weightClass} whitespace-pre-line`}>{body}</p>
          <p className="font-bold">{question}</p>
        </div>
      );
    }
  }

  const lines = normalizedMessage.split("\n");
  if (layout !== "largeFile" || lines.length <= 1) {
    return (
      <div
        id="confirm-dialog-description"
        className={`whitespace-pre-line text-sm leading-relaxed text-[#232946] ${weightClass} ${alignClass}`}
      >
        {lines.map((line, i) => (
          <span key={i}>
            {line}
            {i < lines.length - 1 && <br />}
          </span>
        ))}
      </div>
    );
  }
  const [first, ...rest] = lines;
  return (
    <div
      id="confirm-dialog-description"
      className={`space-y-3 text-sm leading-relaxed text-[#232946] ${alignClass}`}
    >
      <p className={weightClass}>{first}</p>
      {rest.map((line, i) =>
        line ? (
          <p key={i} className="font-bold">
            {line}
          </p>
        ) : null,
      )}
    </div>
  );
}

function ValidationReviewBody({
  message,
  showIntro = true,
}: {
  message: string;
  showIntro?: boolean;
}) {
  const lines = normalizeDialogMessageNewlines(message)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^Missing Required fields:?$/i.test(line));
  const listItems =
    lines.length > 0 ? lines : ["Please correct the highlighted fields."];
  const reviewItems = listItems.reduce<Array<{ text: string; details: string[] }>>(
    (items, line) => {
      const previous = items[items.length - 1];
      if (previous?.text.endsWith(":") && !line.endsWith(":")) {
        previous.details.push(line);
      } else {
        items.push({ text: line, details: [] });
      }
      return items;
    },
    [],
  );

  return (
    <div
      id="confirm-dialog-description"
      className={modalStyles.validationReviewContent}
    >
      {showIntro ? (
        <p className={modalStyles.validationReviewIntro}>
          Please review the required fields below to continue:
        </p>
      ) : null}
      <ul className={modalStyles.validationReviewList}>
        {reviewItems.map((item, index) => {
          return (
            <li
              key={`${item.text}-${index}`}
              className={modalStyles.validationReviewItem}
            >
              <span className={modalStyles.validationReviewIcon} aria-hidden>
                !
              </span>
              <span className={modalStyles.validationReviewText}>
                <span>{item.text}</span>
                {item.details.length > 0 ? (
                  <span className={modalStyles.validationReviewSubList}>
                    {item.details.map((detail) => (
                      <span key={detail}>{detail}</span>
                    ))}
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  /** Raw HTML to render instead of plain text message (e.g. duplicate property links). */
  htmlMessage?: string;
  primaryLabel: string;
  onPrimary: () => void;
  secondaryLabel: string;
  onSecondary: () => void;
  onDismiss?: () => void;
  variant?: "warning" | "info" | "error";
  /** Optional neutral (third) button */
  neutralLabel?: string;
  onNeutral?: () => void;
  hideClose?: boolean;
  /** Large File: first line normal, following lines bold (Angular). */
  /** listingConfirm: body + blank line + bold question (property list Add/Remove Listing). */
  messageLayout?: "default" | "largeFile" | "listingConfirm";
  /**
   * `applicant` – compact layout from modals.module.css (header + X, footer rules).
   * `angular` (default) – structured panel: title bar, body, footer with outline secondary + solid primary.
   */
  shell?: "angular" | "applicant";
  /**
   * Applicant shell only. `simple` – no header/footer divider lines, footer actions centered (ValidationModal-like).
   */
  applicantChrome?: "default" | "simple";
  /** Optional E2E hooks (invitee and other flows may pass distinct values per instance). */
  overlayDataTestId?: string;
  primaryButtonDataTestId?: string;
  secondaryButtonDataTestId?: string;
  neutralButtonDataTestId?: string;
  closeButtonDataTestId?: string;
  /**
   * When true, primary (Save / Yes / OK) renders before secondary (Cancel / No) — left then right.
   * Default false keeps legacy order (secondary left, primary right) for existing callers.
   */
  primaryFirst?: boolean;
  /** Wider responsive confirm card for three-action dialogs. */
  size?: "compact" | "wide";
  /** Slightly wider save-changes shell (section submit titles). */
  saveChangesWidth?: "default" | "comfortable";
  /**
   * Opt-in: make all action buttons equal width (each `flex: 1 1 0`). Default off so
   * existing dialogs are unaffected. Used for multi-action import confirms where the
   * neutral button would otherwise size to its (longer) label.
   */
  equalWidthActions?: boolean;
  /** Render neutral as solid blue and secondary as blue outline. */
  actionTone?: "default" | "blue";
  /** Use application module modal action button styling for scoped confirms. */
  actionStyle?: "default" | "application";
  /**
   * `saveChanges` is the shared Yes/No confirmation style. `centeredPillYesNo`
   * and `compactAngular` are kept as compatibility aliases for older callers.
   * `confirmation` — panel header/body/footer, left-aligned copy, Cancel + Confirm
   * right-aligned (applicant dashboard selection confirm).
   * `default` keeps the older panel + solid primary look for non-Yes/No actions.
   */
  presentation?:
    | "default"
    | "confirmation"
    | "centeredPillYesNo"
    | "compactAngular"
    | "saveChanges"
    | "validationReview";
  /** Hide the default intro copy in `presentation="validationReview"`. */
  hideValidationReviewIntro?: boolean;
  /** Add the shared grey-blue header and footer section chrome. */
  sectionedChrome?: boolean;
  /** Spinner in the primary button while its action is in flight. */
  primaryLoading?: boolean;
  /** Spinner in the secondary button while its action is in flight. */
  secondaryLoading?: boolean;
  /** Disable the primary action. */
  disabled?: boolean;
  /** Override the presentation's default body alignment (e.g. left-align a long, multi-paragraph `saveChanges` message). */
  messageAlign?: "left" | "center";
  /** Override the body text weight — `normal` for longer copy that shouldn't compete with the title. */
  messageWeight?: "normal" | "medium";
  /** Override the presentation's default primary button classes entirely (e.g. match a specific module's standard action button). */
  primaryButtonClassName?: string;
  /** Extra classes applied to the title `<h3>` — use `!` modifiers to beat the CSS-module element selector (e.g. shrink an oversized `saveChanges` title for long dynamic copy). */
  titleClassName?: string;
}

function isYesNoConfirm(primaryLabel: string, secondaryLabel: string): boolean {
  return (
    primaryLabel.trim().toLowerCase() === "yes" &&
    secondaryLabel.trim().toLowerCase() === "no"
  );
}

function ButtonLoadingLabel({
  label,
  spinnerClassName,
}: {
  label: string;
  spinnerClassName: string;
}) {
  return (
    <span className="inline-flex items-center justify-center gap-2">
      <svg
        className={`h-4 w-4 animate-spin ${spinnerClassName}`}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      <span>{label}</span>
    </span>
  );
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  htmlMessage,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  onDismiss,
  neutralLabel,
  onNeutral,
  hideClose = false,
  messageLayout = "default",
  /** Default matches rent confirmation / compact confirm shell (no heavy header/footer rules). */
  applicantChrome = "simple",
  overlayDataTestId,
  primaryButtonDataTestId,
  secondaryButtonDataTestId,
  neutralButtonDataTestId,
  closeButtonDataTestId,
  primaryFirst = false,
  size = "compact",
  saveChangesWidth = "default",
  actionTone = "default",
  actionStyle = "default",
  presentation,
  hideValidationReviewIntro = false,
  equalWidthActions = false,
  sectionedChrome = false,
  primaryLoading = false,
  secondaryLoading = false,
  disabled = false,
  messageAlign,
  messageWeight,
  primaryButtonClassName,
  titleClassName,
}: ConfirmDialogProps) {
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (onDismiss) onDismiss();
      else onSecondary();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onDismiss, onSecondary]);

  if (!isOpen) return null;

  const handleBackdropOrClose = () => {
    if (onDismiss) onDismiss();
    else onSecondary();
  };

  if (title.toLowerCase().includes('l1 review')) {
    const lines = normalizeDialogMessageNewlines(message)
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);

    return (
      <OnScreenModal
        isOpen={isOpen}
        title={title}
        onClose={handleBackdropOrClose}
        maxWidth="max-w-xl"
        overlayDataTestId={overlayDataTestId}
        closeButtonDataTestId={closeButtonDataTestId}
        footer={
          <div className={`${ledgerStyles.footer} ${ledgerStyles.centeredFooter}`}>
            <button
              type="button"
              onClick={onPrimary}
              data-testid={primaryButtonDataTestId}
              className={`${ledgerStyles.yesButton} ${ledgerStyles.l1ReviewOkButton}`}
            >
              {primaryLabel || 'Ok'}
            </button>
          </div>
        }
      >
        <div className={ledgerStyles.body}>
          <div className={ledgerStyles.headingRow}>
            <span className={ledgerStyles.headingIcon} aria-hidden="true">
              <FileText className="h-6 w-6" />
            </span>
            <h4 className={ledgerStyles.headingTitle}>L1 Review Requirement</h4>
          </div>
          <div className={ledgerStyles.noteBox}>
            {lines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </div>
      </OnScreenModal>
    );
  }

  const usesSharedYesNoPresentation =
    presentation !== "confirmation" &&
    (presentation === "centeredPillYesNo" ||
      presentation === "compactAngular" ||
      ((presentation === undefined || presentation === "default") &&
        (isYesNoConfirm(primaryLabel, secondaryLabel) ||
          (primaryFirst && secondaryLabel.trim().toLowerCase() === "no"))));
  const normalizedPresentation =
    presentation === "validationReview"
      ? "validationReview"
      : presentation === "confirmation"
        ? "confirmation"
        : usesSharedYesNoPresentation
          ? "saveChanges"
          : presentation ?? "default";
  const saveChanges = normalizedPresentation === "saveChanges";
  const validationReview = normalizedPresentation === "validationReview";
  const confirmationPanel =
    normalizedPresentation === "confirmation" ||
    (normalizedPresentation === "default" && applicantChrome === "default");
  const showCloseButton = !hideClose;
  const useApplicationActions = actionStyle === "application";
  const isThreeActionDialog = Boolean(neutralLabel && onNeutral);
  const isThreeActionSaveChanges = saveChanges && isThreeActionDialog;

  const baseHeaderClass = validationReview
    ? modalStyles.modalValidationReviewHeader
    : saveChanges
      ? modalStyles.modalSaveChangesHeader
      : confirmationPanel
        ? modalStyles.modalHeader
        : applicantChrome === "simple"
          ? modalStyles.modalHeaderConfirmTight
          : modalStyles.modalHeader;
  const baseBodyClass = validationReview
    ? modalStyles.modalValidationReviewBody
    : saveChanges
      ? modalStyles.modalSaveChangesBody
      : confirmationPanel
        ? modalStyles.modalBody
        : applicantChrome === "simple"
          ? modalStyles.modalBodyConfirmTight
          : modalStyles.modalBody;
  const baseFooterClass = validationReview
    ? modalStyles.modalValidationReviewFooter
    : saveChanges
      ? modalStyles.modalSaveChangesFooter
      : useApplicationActions
        ? `${modalStyles.modalFooter} ${modalStyles.modalFooterCentered}`
        : confirmationPanel
          ? modalStyles.modalFooter
          : applicantChrome === "simple"
            ? modalStyles.modalFooterConfirmTight
            : `${modalStyles.modalFooter} ${modalStyles.modalFooterCentered}`;

  const headerClass = `${baseHeaderClass}${sectionedChrome ? ` ${claimsModalStyles.modalShellHeaderChromeSectioned}` : ""}`;
  // Centered sectioned body when explicitly requested (e.g. product confirms).
  // Default sectioned chrome stays left-aligned for resident/reimbursement.
  const sectionedBodyClass =
    sectionedChrome && saveChanges
      ? ` ${claimsModalStyles.modalSaveChangesBodySectioned}${
          messageAlign === "center"
            ? ` ${claimsModalStyles.modalSaveChangesBodySectionedCenter}`
            : ""
        }`
      : "";
  const bodyClass = `${baseBodyClass}${sectionedBodyClass}`;
  const footerClass = `${baseFooterClass}${sectionedChrome ? ` ${claimsModalStyles.modalShellFooterChromeSectioned}` : ""}`;

  const resolvedMessageAlign: "left" | "center" =
    messageAlign ??
    (saveChanges && !sectionedChrome ? "center" : "left");
  const isCenteredMessage = resolvedMessageAlign === "center";

  const primaryBtnClass =
    primaryButtonClassName ??
    (validationReview
      ? modalStyles.btnValidationReviewPrimary
      : saveChanges
        ? modalStyles.btnSaveChangesPrimary
        : useApplicationActions
          ? `${modalStyles.modalActionPrimary} ${modalStyles.modalActionConfirmOrderReset}`
          : modalStyles.btnPrimary);
  const secondaryBtnClass = validationReview
    ? modalStyles.btnValidationReviewSecondary
    : saveChanges
      ? isThreeActionSaveChanges
        ? `${modalStyles.modalActionSecondary} ${modalStyles.modalActionConfirmOrderReset}`
        : modalStyles.btnSaveChangesSecondary
      : useApplicationActions
        ? `${modalStyles.modalActionSecondary} ${modalStyles.modalActionConfirmOrderReset}`
        : actionTone === "blue"
        ? modalStyles.btnSecondaryBlue
        : modalStyles.btnSecondary;
  const neutralBtnClass = saveChanges
    ? modalStyles.btnSaveChangesPrimary
    : useApplicationActions
      ? `${modalStyles.modalActionPrimary} ${modalStyles.modalActionConfirmOrderReset}`
      : actionTone === "blue"
        ? modalStyles.btnPrimary
        : modalStyles.btnSecondary;
  const contentSizeClass =
    size === "wide" ? modalStyles.modalContentConfirmWide : "";
  const effectivePrimaryFirst = validationReview
    ? false
    : saveChanges
      ? true
      : primaryFirst;

  const actionsBusy = primaryLoading || secondaryLoading;

  const primaryActionButton = (
    <button
      type="button"
      disabled={disabled || actionsBusy}
      onClick={onPrimary}
      data-testid={primaryButtonDataTestId}
      className={`${primaryBtnClass} disabled:opacity-60 disabled:cursor-not-allowed`}
    >
      {primaryLoading ? (
        <ButtonLoadingLabel label={primaryLabel} spinnerClassName="text-white" />
      ) : (
        primaryLabel
      )}
    </button>
  );

  const secondaryActionButton = secondaryLabel ? (
    <button
      type="button"
      disabled={disabled || actionsBusy}
      onClick={onSecondary}
      data-testid={secondaryButtonDataTestId}
      className={`${secondaryBtnClass} disabled:opacity-60 disabled:cursor-not-allowed`}
    >
      {secondaryLoading ? (
        <ButtonLoadingLabel label={secondaryLabel} spinnerClassName="text-slate-600" />
      ) : (
        secondaryLabel
      )}
    </button>
  ) : null;

  const actionButtons = effectivePrimaryFirst ? (
    <>
      {primaryActionButton}
      {neutralLabel && onNeutral ? (
        <button
          type="button"
          disabled={actionsBusy}
          onClick={onNeutral}
          data-testid={neutralButtonDataTestId}
          className={neutralBtnClass}
        >
          {neutralLabel}
        </button>
      ) : null}
      {secondaryActionButton}
    </>
  ) : (
    <>
      {secondaryActionButton}
      {neutralLabel && onNeutral ? (
        <button
          type="button"
          disabled={actionsBusy}
          onClick={onNeutral}
          data-testid={neutralButtonDataTestId}
          className={neutralBtnClass}
        >
          {neutralLabel}
        </button>
      ) : null}
      {primaryActionButton}
    </>
  );

  const content = (
    <div
      className={`${modalStyles.modalOverlay} !z-[2147483640]`}
      onClick={handleBackdropOrClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby={
        htmlMessage || message ? "confirm-dialog-description" : undefined
      }
      data-testid={overlayDataTestId}
    >
      <div
        className={`${modalStyles.modalContent} ${modalStyles.modalContentCompact}${contentSizeClass ? ` ${contentSizeClass}` : ""}${saveChanges ? ` ${modalStyles.modalContentSaveChanges}` : ""}${saveChanges && saveChangesWidth === "comfortable" ? ` ${modalStyles.modalContentSaveChangesComfortable}` : ""}${validationReview ? ` ${modalStyles.modalContentValidationReview}` : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={headerClass}>
          <h3 id="confirm-dialog-title" className={titleClassName}>{title}</h3>
          {showCloseButton ? (
            <button
              type="button"
              onClick={handleBackdropOrClose}
              data-testid={closeButtonDataTestId}
              className={modalStyles.closeButton}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>
        <div className={bodyClass}>
          {validationReview ? (
            <ValidationReviewBody message={message} showIntro={!hideValidationReviewIntro} />
          ) : htmlMessage ? (
            <div
              id="confirm-dialog-description"
              className={`text-sm leading-relaxed text-[#232946] [&_a]:text-blue-600 [&_a]:underline [&_strong]:font-bold ${isCenteredMessage ? "text-center" : "text-left"}`}
              dangerouslySetInnerHTML={{ __html: htmlMessage }}
            />
          ) : (
            <DialogMessageBodyApplicant
              message={message}
              layout={messageLayout}
              align={resolvedMessageAlign}
              weight={
                messageWeight ?? (sectionedChrome ? "normal" : "medium")
              }
            />
          )}
        </div>
        <div className={footerClass} role="group" aria-label="Confirmation">
          {useApplicationActions ? (
            <div
              className={`${modalStyles.modalFooterActionGroup}${equalWidthActions ? ` ${modalStyles.modalFooterActionGroupEqual}` : ""}`}
            >
              {actionButtons}
            </div>
          ) : (
            actionButtons
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
